import { useEffect, useState } from "react";
import { Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { AdminGuard, AdminTitle } from "@/components/admin/AdminGuard";
import { errText, getDrops } from "@/lib/api";
import { adminDeleteProduct, adminProducts, adminSaveProduct, adminUpload } from "@/lib/admin";
import { cad, CATEGORIES, totalStock } from "@/lib/site";

const APPAREL = ["XS", "S", "M", "L", "XL", "XXL"];
const BLANK = { name: "", slug: "", price: 49, category: "tees", description: "", tagline: "", badge: "", featured: false, drop_slug: "", images: [], colors: [{ name: "Sus Black", hex: "#111111", images: [] }], sizes: Object.fromEntries(APPAREL.map((s) => [s, 10])) };

const Uploader = ({ onAdd, testid, label = "Upload" }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    setBusy(true);
    for (const f of files) {
      try { const r = await adminUpload(f); onAdd({ src: r.src, alt: "" }); } catch (err) { toast.error(errText(err)); }
    }
    setBusy(false);
  };
  return (
    <label className="label inline-flex cursor-pointer items-center gap-2 border border-dashed border-ink/50 px-3 py-2 font-bold hover:bg-lime" data-testid={testid}>
      <Upload size={13} /> {busy ? "Uploading…" : label}
      <input type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={pick} className="sr-only" data-testid={`${testid}-input`} />
    </label>
  );
};

const Thumbs = ({ images, onRemove, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {images.map((im, i) => (
      <div key={im.src + i} className="tile relative h-16 w-16">
        <img src={im.src} alt="" className="h-full w-full object-cover mix-blend-multiply" />
        <button type="button" onClick={() => onRemove(i)} aria-label="Remove image" className="absolute -right-1.5 -top-1.5 rounded-full bg-ink p-0.5 text-cloud" data-testid={`${testid}-remove-${i}`}><X size={11} /></button>
      </div>
    ))}
  </div>
);

const ColourRow = ({ c, i, update, remove }) => (
  <div className="flex flex-wrap items-start gap-4 border-b border-ink/10 py-3" data-testid={`pe-colour-${i}`}>
    <input type="color" value={c.hex} onChange={(e) => update({ ...c, hex: e.target.value.toUpperCase() })} className="h-10 w-10 cursor-pointer border border-ink/30 bg-transparent" data-testid={`pe-colour-hex-${i}`} />
    <input value={c.name} onChange={(e) => update({ ...c, name: e.target.value })} placeholder="Colour name" className="field !w-40" data-testid={`pe-colour-name-${i}`} />
    <div className="flex flex-1 flex-wrap items-center gap-3">
      <Thumbs images={c.images} onRemove={(k) => update({ ...c, images: c.images.filter((_, j) => j !== k) })} testid={`pe-colour-images-${i}`} />
      <Uploader label="Colour photo" onAdd={(im) => update({ ...c, images: [...c.images, { ...im, alt: c.name }] })} testid={`pe-colour-upload-${i}`} />
    </div>
    <button type="button" onClick={remove} aria-label="Remove colour" className="p-2 text-ink/60 hover:text-alert" data-testid={`pe-colour-remove-${i}`}><Trash2 size={15} /></button>
  </div>
);

const Editor = ({ initial, drops, onDone, onCancel }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const oneSize = Object.keys(f.sizes).join() === "OS";
  const editing = !!initial.slug && initial.__existing;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { __existing, ...body } = f;
    try {
      const r = await adminSaveProduct(editing ? initial.slug : null, { ...body, price: Number(body.price), slug: body.slug || null, drop_slug: body.drop_slug || null, badge: body.badge || null, sizes: Object.fromEntries(Object.entries(body.sizes).map(([k, v]) => [k, Number(v) || 0])) });
      toast.success(`${r.name} saved`);
      onDone();
    } catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  const input = (k, label, props = {}) => <label className="block"><span className="label text-ink/60">{label}</span><input value={f[k]} onChange={(e) => set(k, e.target.value)} className="field" data-testid={`pe-${k}`} {...props} /></label>;
  return (
    <form onSubmit={save} className="mt-8 space-y-8 border-[1.5px] border-ink bg-white p-6" data-testid="product-editor">
      <div className="flex items-center justify-between"><p className="label font-bold">{editing ? `Edit ${initial.name}` : "New product"}</p><button type="button" onClick={onCancel} className="link-u" data-testid="pe-cancel">Cancel</button></div>
      <div className="grid gap-6 md:grid-cols-4">
        <div className="md:col-span-2">{input("name", "Name", { required: true })}</div>
        {input("slug", "Slug (auto if empty)", { disabled: editing })}
        {input("price", "Price (CAD)", { type: "number", step: "0.01", min: "1", required: true })}
        <label className="block"><span className="label text-ink/60">Category</span><select value={f.category} onChange={(e) => set("category", e.target.value)} className="field" data-testid="pe-category">{CATEGORIES.filter((c) => c.id !== "all").map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select></label>
        <label className="block"><span className="label text-ink/60">Drop</span><select value={f.drop_slug || ""} onChange={(e) => set("drop_slug", e.target.value)} className="field" data-testid="pe-drop"><option value="">None</option>{drops.map((d) => <option key={d.slug} value={d.slug}>{d.number} {d.name}</option>)}</select></label>
        {input("tagline", "Tagline")}
        {input("badge", "Badge (e.g. New)")}
        <label className="block md:col-span-3"><span className="label text-ink/60">Description</span><textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} className="field" data-testid="pe-description" /></label>
        <label className="flex items-center gap-3 self-end pb-3"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="pe-featured" /><span className="label font-bold">Featured</span></label>
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-4"><p className="label font-bold">Stock per size</p>
          <button type="button" onClick={() => set("sizes", oneSize ? Object.fromEntries(APPAREL.map((s) => [s, 10])) : { OS: 20 })} className="link-u" data-testid="pe-size-mode">{oneSize ? "Use XS–XXL" : "Use one size"}</button></div>
        <div className="mt-3 flex flex-wrap gap-4">{Object.entries(f.sizes).map(([s, n]) => (
          <label key={s} className="w-20"><span className="label text-ink/60">{s}</span><input type="number" min="0" value={n} onChange={(e) => set("sizes", { ...f.sizes, [s]: e.target.value })} className="field font-mono" data-testid={`pe-stock-${s}`} /></label>
        ))}</div>
      </div>
      <div>
        <p className="label font-bold">Main images</p>
        <div className="mt-3 flex flex-wrap items-center gap-3"><Thumbs images={f.images} onRemove={(i) => set("images", f.images.filter((_, j) => j !== i))} testid="pe-images" /><Uploader onAdd={(im) => setF((x) => ({ ...x, images: [...x.images, { ...im, alt: x.name }] }))} testid="pe-upload" /></div>
      </div>
      <div>
        <div className="flex items-center gap-4"><p className="label font-bold">Colours</p><button type="button" onClick={() => set("colors", [...f.colors, { name: "", hex: "#FF7EC7", images: [] }])} className="link-u" data-testid="pe-add-colour"><Plus size={13} /> Add colour</button></div>
        <p className="mt-1 text-[12px] text-ink/60">Colour photos replace the main photo when a shopper picks that colour.</p>
        <div className="mt-2">{f.colors.map((c, i) => <ColourRow key={i} c={c} i={i} update={(nc) => set("colors", f.colors.map((x, j) => (j === i ? nc : x)))} remove={() => set("colors", f.colors.filter((_, j) => j !== i))} />)}</div>
      </div>
      <button disabled={busy || !f.colors.length} className="btn btn-lime" data-testid="pe-save">{busy ? "Saving…" : "Save product"}</button>
    </form>
  );
};

const Panel = () => {
  const [rows, setRows] = useState(null);
  const [drops, setDrops] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => adminProducts().then(setRows).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); getDrops().then(setDrops).catch(() => {}); }, []);
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}? This removes it from the shop.`)) return;
    try { await adminDeleteProduct(p.slug); toast.success("Deleted"); load(); } catch (e) { toast.error(errText(e)); }
  };
  const edit = (p) => setEditing({ ...BLANK, ...p, badge: p.badge || "", drop_slug: p.drop_slug || "", colors: p.colors.map((c) => ({ ...c, images: c.images || [] })), __existing: true });
  return (
    <>
      <AdminTitle right={<button onClick={() => setEditing({ ...BLANK })} className="btn btn-ink" data-testid="admin-new-product"><Plus size={16} /> New product</button>}>Products</AdminTitle>
      {editing && <Editor key={editing.slug || "new"} initial={editing} drops={drops} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      <table className="mt-8 w-full text-left text-sm" data-testid="admin-products-table">
        <thead><tr className="label border-b border-ink/15 text-ink/60"><th className="py-3">Product</th><th>Price</th><th className="hidden md:table-cell">Category</th><th>Stock</th><th className="hidden md:table-cell">Colours</th><th /></tr></thead>
        <tbody>{rows?.map((p) => (
          <tr key={p.slug} className="border-b border-ink/10" data-testid={`admin-product-${p.slug}`}>
            <td className="py-3"><div className="flex items-center gap-3"><img src={p.images[0]?.src} alt="" className="tile h-12 w-12 object-cover mix-blend-multiply" /><div><p>{p.name}</p><p className="font-mono text-[11px] text-ink/60">{p.slug}{p.featured && " · featured"}</p></div></div></td>
            <td>{cad(p.price)}</td><td className="hidden md:table-cell">{p.category}</td>
            <td className={totalStock(p) <= 5 ? "text-alert" : ""}>{totalStock(p)}</td>
            <td className="hidden md:table-cell"><div className="flex gap-1">{p.colors.map((c) => <span key={c.name} title={c.name} className="h-3 w-3 rounded-full ring-1 ring-ink/30" style={{ background: c.hex }} />)}</div></td>
            <td className="space-x-4 text-right"><button onClick={() => edit(p)} className="link-u" data-testid={`admin-edit-${p.slug}`}>Edit</button><button onClick={() => del(p)} className="link-u text-alert" data-testid={`admin-delete-${p.slug}`}>Delete</button></td>
          </tr>
        ))}</tbody>
      </table>
    </>
  );
};

export default function AdminProducts() {
  return <AdminGuard><Panel /></AdminGuard>;
}
